import { useEffect, useRef, useState } from 'react'
import Icon from './Icon'
import HTMLReactParser from 'html-react-parser'
import useCancelModal from '../hooks/useCancelModal'

interface IPropertyBar {
  children: any
  entityID: string | null
  isVisible: boolean
  onSave: any
  onCancel: any
}

const PropertyBar = ({ children, entityID, isVisible, onSave, onCancel }: IPropertyBar) => {
  const navRef = useRef<HTMLElement | null>(null)
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (): Promise<boolean> => {
    if (!validate()) {
      return false
    }

    await onSave()
      .then((result: any) => {
        handleCollapse()
        flashRow(entityID!)
        return true
      })
      .catch((error: any) => {
        if (error.response) {
          const apiError = JSON.parse(error.response)

          if (apiError.message) {
            setErrorMessage(apiError.message)
          } else {
            setErrorMessage(error.response)
          }
        }

        return false
      })

    return false
  }

  const validate = (): boolean => {
    // Trigger validation on all TextInput components
    const validateEvent = new CustomEvent('validateInputs')
    window.dispatchEvent(validateEvent)

    // Check for invalid inputs after a brief delay to allow validation to complete
    // We need to check both the class (which is set after validationAttempted) and
    // also check required fields directly
    const requiredInputs = document.querySelectorAll('input[required], textarea[required], select[required]')
    let hasInvalid = false

    requiredInputs.forEach((input: Element) => {
      const htmlInput = input as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      if (!htmlInput.value || htmlInput.value.trim().length === 0) {
        hasInvalid = true
      }
    })

    // Also check for elements with not-valid class (for fields that have been validated)
    const invalidInputs = document.getElementsByClassName('not-valid')
    if (invalidInputs.length > 0 || hasInvalid) {
      setErrorMessage('')
      return false
    } else {
      setErrorMessage('')
      return true
    }
  }

  const flashRow = (id: string) => {
    const row = document.getElementById(`row-${id}`)

    if (row) {
      row.classList.add('flash-row')
      setTimeout(() => {
        row.classList.remove('flash-row')
      }, 750)
    }
  }

  useEffect(() => {
    if (isVisible) {
      navRef.current?.classList.add('opened')
    }
  }, [isVisible])

  const handleCollapse = () => {
    navRef.current?.classList.remove('opened')
    onCancel()
  }

  useEffect(() => {
    setErrorMessage('')
    // Clear validation state when property bar opens/closes
    const clearValidationEvent = new CustomEvent('clearValidation')
    window.dispatchEvent(clearValidationEvent)
  }, [entityID, isVisible])

  useCancelModal(navRef, handleCollapse)

  return (
    <nav ref={navRef} id="property-bar" className={`property-bar${isVisible ? '' : ' collapsed'}`}>
      <form onSubmit={handleSubmit}>
        <div className="collapse-button" onClick={handleCollapse}>
          <Icon name="AngleDoubleRight"></Icon>
        </div>
        {children}
        <div className="row buttons">
          <div className="col-6">
            <button disabled={false} type="button" onClick={handleSubmit} className="styled-button">
              Save
            </button>
          </div>
          <div className="col-6">
            <button disabled={false} type="button" onClick={handleCollapse} className="styled-button cancel">
              Cancel
            </button>
          </div>
        </div>
        <div className="error-message">{HTMLReactParser(errorMessage)}</div>
      </form>
    </nav>
  )
}

export default PropertyBar
